import { z } from 'zod';
import { chatTriggerSourceSchema } from './chat';
import { ResourceType } from '../resource';
import type {
  ApiSchemaClass,
  ApiSchemaDefinition,
  ApiSchemaMapperClass,
  ApiSchemaMapperDefinition,
} from './types';
import {
  emptyObjectResponseSchema,
  folderItemsResponseSchema,
  folderOutputResponseSchema,
} from './common-responses';

const emptyRequestBodySchema = z.object({});

const folderScopeSchema = z.enum(['private', 'shared']);
const folderCreateScopeSchema = z.enum(['user', 'org']);
const sortDirSchema = z.enum(['asc', 'desc']);
const folderItemTypeSchema = z.nativeEnum(ResourceType);

const optionalQueryEnumArraySchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.preprocess((value) => {
    if (value === undefined || value === '') {
      return undefined;
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [value];
  }, z.array(itemSchema).optional());

export const folderCreateBodyLegacySchema = z.object({
  name: z.string(),
  parentId: z.string().nullable(),
  scope: folderCreateScopeSchema,
});

export type FolderCreateBodyLegacy = z.infer<
  typeof folderCreateBodyLegacySchema
>;

export const folderCreateBodyV20260601Schema = folderCreateBodyLegacySchema;

export type FolderCreateBodyV20260601 = z.infer<
  typeof folderCreateBodyV20260601Schema
>;

export const folderRenameBodyLegacySchema = z.object({
  name: z.string(),
});

export type FolderRenameBodyLegacy = z.infer<
  typeof folderRenameBodyLegacySchema
>;

export const folderRenameBodyV20260601Schema = folderRenameBodyLegacySchema;

export type FolderRenameBodyV20260601 = z.infer<
  typeof folderRenameBodyV20260601Schema
>;

export const folderMoveBodyLegacySchema = z.object({
  newParentId: z.string().nullable(),
});

export type FolderMoveBodyLegacy = z.infer<typeof folderMoveBodyLegacySchema>;

export const folderMoveBodyV20260601Schema = folderMoveBodyLegacySchema;

export type FolderMoveBodyV20260601 = z.infer<
  typeof folderMoveBodyV20260601Schema
>;

export const folderItemsQueryLegacySchema = z.object({
  folderScope: folderScopeSchema.optional(),
  entityType: folderItemTypeSchema.optional(),
  itemTypes: optionalQueryEnumArraySchema(folderItemTypeSchema),
  search: z.string().optional(),
  sort: z.string().optional(),
  sortDir: sortDirSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  unreadOnly: z.coerce.boolean().optional(),
  triggerSources: optionalQueryEnumArraySchema(chatTriggerSourceSchema),
});

export type FolderItemsQueryLegacy = z.infer<
  typeof folderItemsQueryLegacySchema
>;

export const folderItemsQueryV20260601Schema = folderItemsQueryLegacySchema;

export type FolderItemsQueryV20260601 = z.infer<
  typeof folderItemsQueryV20260601Schema
>;

export class FolderCreateLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'folder-create-legacy' as const;

  readonly name = FolderCreateLegacySchema.version;
  readonly schema = folderCreateBodyLegacySchema;
  readonly response = folderOutputResponseSchema;
}

export class FolderCreateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'folder-create-2026-06-01' as const;

  readonly name = FolderCreateV20260601Schema.version;
  readonly schema = folderCreateBodyV20260601Schema;
  readonly response = folderOutputResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [FolderCreateLegacyToV20260601Mapper];
  }
}

export class FolderCreateLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = FolderCreateLegacySchema.version;
  readonly toVersion = FolderCreateV20260601Schema.version;

  body(body: FolderCreateBodyLegacy): FolderCreateBodyV20260601 {
    return body;
  }
}

export class FolderRenameLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'folder-rename-legacy' as const;

  readonly name = FolderRenameLegacySchema.version;
  readonly schema = folderRenameBodyLegacySchema;
  readonly response = emptyObjectResponseSchema;
}

export class FolderRenameV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'folder-rename-2026-06-01' as const;

  readonly name = FolderRenameV20260601Schema.version;
  readonly schema = folderRenameBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [FolderRenameLegacyToV20260601Mapper];
  }
}

export class FolderRenameLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = FolderRenameLegacySchema.version;
  readonly toVersion = FolderRenameV20260601Schema.version;

  body(body: FolderRenameBodyLegacy): FolderRenameBodyV20260601 {
    return body;
  }
}

export class FolderMoveLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'folder-move-legacy' as const;

  readonly name = FolderMoveLegacySchema.version;
  readonly schema = folderMoveBodyLegacySchema;
  readonly response = emptyObjectResponseSchema;
}

export class FolderMoveV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'folder-move-2026-06-01' as const;

  readonly name = FolderMoveV20260601Schema.version;
  readonly schema = folderMoveBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [FolderMoveLegacyToV20260601Mapper];
  }
}

export class FolderMoveLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = FolderMoveLegacySchema.version;
  readonly toVersion = FolderMoveV20260601Schema.version;

  body(body: FolderMoveBodyLegacy): FolderMoveBodyV20260601 {
    return body;
  }
}

export class FolderItemsLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'folder-items-legacy' as const;

  readonly name = FolderItemsLegacySchema.version;
  readonly schema = emptyRequestBodySchema;
  readonly query = folderItemsQueryLegacySchema;
  readonly response = folderItemsResponseSchema;
}

export class FolderItemsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'folder-items-2026-06-01' as const;

  readonly name = FolderItemsV20260601Schema.version;
  readonly schema = emptyRequestBodySchema;
  readonly query = folderItemsQueryV20260601Schema;
  readonly response = folderItemsResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [FolderItemsLegacyToV20260601Mapper];
  }
}

export class FolderItemsLegacyToV20260601Mapper
  implements ApiSchemaMapperDefinition
{
  readonly fromVersion = FolderItemsLegacySchema.version;
  readonly toVersion = FolderItemsV20260601Schema.version;

  queryParams(query: FolderItemsQueryLegacy): FolderItemsQueryV20260601 {
    return query;
  }
}

export const folderLibraryRootQueryV20260601Schema = z.object({
  ownership: z
    .enum(['mine', 'shared'])
    .optional()
    .describe(
      'Ownership lens: `mine` = owned by the actor; `shared` = inbound external shares (org-wide excluded)'
    ),
  search: z.string().optional(),
  sort: z.string().optional(),
  sortDir: sortDirSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type FolderLibraryRootQueryV20260601 = z.infer<
  typeof folderLibraryRootQueryV20260601Schema
>;

/**
 * Top-level Library listing: root folders (floated to top) + unfiled artifacts,
 * filtered by the ownership lens and paginated server-side.
 */
export class FolderLibraryRootV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'folder-library-root-2026-06-01' as const;

  readonly name = FolderLibraryRootV20260601Schema.version;
  readonly schema = emptyRequestBodySchema;
  readonly query = folderLibraryRootQueryV20260601Schema;
  readonly response = folderItemsResponseSchema;
}

export const FOLDER_API_SCHEMAS = [
  FolderCreateLegacySchema,
  FolderCreateV20260601Schema,
  FolderRenameLegacySchema,
  FolderRenameV20260601Schema,
  FolderMoveLegacySchema,
  FolderMoveV20260601Schema,
  FolderItemsLegacySchema,
  FolderItemsV20260601Schema,
  FolderLibraryRootV20260601Schema,
] as const satisfies readonly ApiSchemaClass[];
